import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from './routes/ProtectedRoute';
import { AppLayout } from './components/AppLayout';
import LoginPage from './pages/LoginPage';
import PeoplePage from './pages/PeoplePage';
import TasksPage from './pages/TasksPage';
import AssignedToMePage from './pages/AssignedToMePage';
import AssignedByMePage from './pages/AssignedByMePage';
import ImportantPage from './pages/ImportantPage';
import MyDayPage from './pages/MyDayPage';
import PlannedPage from './pages/PlannedPage';
import OverviewPage from './pages/OverviewPage';
import ListsPage from './pages/ListsPage';
import { useAuth } from './hooks/useAuth';
import DepartmentsPage from './pages/DepartmentsPage';
import RecipientGroupsPage from './pages/RecipientGroupsPage';
import ProjectsPage from './pages/ProjectsPage';
import SettingsPage from './pages/SettingsPage';
import CalendarPage from './pages/CalendarPage';
import AuditPage from './pages/AuditPage';
import StakeholdersPage from './pages/StakeholdersPage';
import SignPage from './pages/SignPage';
import FolderPage from './pages/FolderPage';
import PublicSignPage from './pages/PublicSignPage';
import { SystemAuthProvider } from './system/SystemAuthContext';
import { SystemRoute } from './system/SystemRoute';
import { SystemLayout } from './system/SystemLayout';
import SystemLoginPage from './system/SystemLoginPage';
import OrganisationsPage from './system/OrganisationsPage';
import OperatorsPage from './system/OperatorsPage';
import SystemAuditPage from './system/AuditPage';
import PlatformSettingsPage from './system/PlatformSettingsPage';

/**
 * "/" is not a page any more - it is a signpost. Admins land on the
 * Dashboard (their job is oversight), everyone else on My Day (their job
 * is today's work). Sign-in and every stray URL funnel through here.
 */
function HomeRedirect() {
  const { user } = useAuth();
  const isAdmin =
    user?.role.systemKey === 'ADMIN' || user?.role.systemKey === 'SUPER_ADMIN';
  return <Navigate to={isAdmin ? '/dashboard' : '/my-day'} replace />;
}

/**
 * The route table.
 *
 * Two layers of nesting, each doing one job:
 *   ProtectedRoute -> is there a session? if not, redirect to /login
 *   AppLayout      -> the sidebar shell, rendered once around every page
 *
 * Because AppLayout is a layout route rather than a wrapper inside each page,
 * the sidebar keeps its state across navigation instead of remounting.
 */
export default function App() {
  return (
    <Routes>
      {/* --- public --- */}
      <Route path="/login" element={<LoginPage />} />
      {/* CG Sign's recipient page: the token in the URL is the auth. */}
      <Route path="/sign/:token" element={<PublicSignPage />} />

      {/* --- authenticated --- */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/dashboard" element={<OverviewPage />} />
          <Route path="/departments" element={<DepartmentsPage />} />
          <Route path="/recipient-groups" element={<RecipientGroupsPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/overview" element={<Navigate to="/dashboard" replace />} />
          <Route path="/my-day" element={<MyDayPage />} />
          <Route path="/planned" element={<PlannedPage />} />
          <Route path="/people" element={<PeoplePage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/lists" element={<ListsPage />} />
          <Route path="/assigned-to-me" element={<AssignedToMePage />} />
          <Route path="/assigned-by-me" element={<AssignedByMePage />} />
          <Route path="/important" element={<ImportantPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/audit" element={<AuditPage />} />
          {/* Admin + Super Admin; the API 403s anyone else who reaches it by URL. */}
          <Route path="/guests" element={<StakeholdersPage />} />
          <Route path="/cg-sign" element={<SignPage />} />
          <Route path="/vault" element={<FolderPage />} />
          {/* The module was called Folder until the rail landed. Kept as a
              redirect so old bookmarks and the FOLDER_SHARED notification
              deep-link keep working. */}
          <Route path="/folder" element={<Navigate to="/vault" replace />} />
          {/* <Route path="/tasks/:id" element={<TaskDetailPage />} /> */}
        </Route>
      </Route>

      {/*
        --- the platform console ---

        OUTSIDE ProtectedRoute and OUTSIDE AppLayout, on purpose. It runs on
        the console session (system_admins), not the tenant one, so wrapping
        it in the tenant guard would demand an organisation login before an
        operator could reach a system that has nothing to do with
        organisations.

        ConsoleAuthProvider sits here rather than at the app root so the
        tenant app never mounts it - the two sessions are independent, and
        a console token is never attached to a tenant request.

        NOT LINKED from anywhere. An operator types the URL; nothing in the
        product advertises that the console exists.
      */}
      <Route path="/system" element={<SystemAuthProvider><Outlet /></SystemAuthProvider>}>
        <Route index element={<Navigate to="/system/organisations" replace />} />
        <Route path="login" element={<SystemLoginPage />} />
        <Route element={<SystemRoute />}>
          <Route element={<SystemLayout />}>
            <Route path="organisations" element={<OrganisationsPage />} />
            <Route path="operators" element={<OperatorsPage />} />
            <Route path="settings" element={<PlatformSettingsPage />} />
            <Route path="audit" element={<SystemAuditPage />} />
          </Route>
        </Route>
      </Route>

      {/* --- anything else --- */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
